import { useEffect, useMemo } from 'react';

import { useStores } from './useStores';

import { createSelectedHook } from '~/core/utils';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectLoadEstimateCode } from '~/redux/slices/estimate';
import {
  selectRestorableStoreId,
  selectSelectedStore,
  updateSelectedStore,
} from '~/redux/slices/store';

export const useSelectedStore = () => {
  const currentSelectedStore = useAppSelector(selectSelectedStore);
  const restorableStoreId = useAppSelector(selectRestorableStoreId);
  const loadEstimateCode = useAppSelector(selectLoadEstimateCode);
  const { stores } = useStores();
  const { fetchedEstimate } = useFetchedEstimate();

  // The authoritative Lead store ID: prefer restorableStoreId (set by the full
  // restore flow) but fall back to the fetched estimate's storeId directly.
  // This fallback is necessary because restoreFromFetchedEstimate may not run
  // (the main loading effect's dedup guard can block it when
  // currentEstimateCode is set before the restore fires).
  const leadStoreId = restorableStoreId ?? fetchedEstimate?.storeId;

  const { selectedValue: computedStore } = createSelectedHook({
    selector: selectSelectedStore,
    restorableSelector: selectRestorableStoreId,
    apiQueryHook: useStores,
    selectRestorableValue: ({
      apiHookResponse,
      restorableValue,
      selectedValue,
    }) =>
      selectedValue ||
      apiHookResponse.stores.find(
        (store) => store.companyId === restorableValue
      ),
    shouldReturnDefault: ({ selectedValue, apiHookResponse }) =>
      !selectedValue ||
      !apiHookResponse.stores.find(
        (store) => store.companyId === selectedValue.companyId
      ),
    selectDefaultValue: ({ apiHookResponse }) => apiHookResponse.stores[0],
  })();

  // Prong 1: Make the Lead's store win at compute time.
  // createSelectedHook skips the restorable path when selectedValue is already
  // a valid in-list store (shouldReturnDefault = false). When the Lead's store
  // is known and differs from the computed store, override with the Lead's store
  // so downstream consumers see the correct value immediately.
  const selectedStore = useMemo(() => {
    if (
      leadStoreId &&
      computedStore &&
      computedStore.companyId !== leadStoreId
    ) {
      const leadStore = stores.find((s) => s.companyId === leadStoreId);
      if (leadStore) {
        return leadStore;
      }
    }
    return computedStore;
  }, [computedStore, leadStoreId, stores]);

  const dispatch = useAppDispatch();

  // Prong 2: Guard the write-back so the distance-default is never persisted
  // while a Lead restore is in progress.
  useEffect(() => {
    if (
      selectedStore &&
      selectedStore.companyId !== currentSelectedStore?.companyId
    ) {
      // Block write-back while a Lead restore is expected but hasn't completed:
      // 1. Estimate is loading but the Lead's store hasn't been determined yet
      const isRestoreExpected = !!loadEstimateCode && !leadStoreId;
      // 2. Lead store is known, computed store doesn't match, and the
      //    Lead's store is actually achievable (exists in the list)
      const isRestorePending =
        !!leadStoreId &&
        selectedStore.companyId !== leadStoreId &&
        stores.some((s) => s.companyId === leadStoreId);

      if (!isRestoreExpected && !isRestorePending) {
        dispatch(updateSelectedStore(selectedStore));
      }
    }
  }, [
    selectedStore,
    dispatch,
    currentSelectedStore?.companyId,
    leadStoreId,
    loadEstimateCode,
    stores,
  ]);

  return selectedStore;
};
